import { FC, useCallback, useEffect, useState } from 'react'
import { LeafletMouseEvent } from 'leaflet'
import { useMap } from 'react-leaflet'
import { Label } from '@/components/ui/label.tsx'
import { Crosshair } from 'lucide-react'
import { Button } from '@/components/ui/button.tsx'
import { useAtomValue, useSetAtom } from 'jotai'
import { isSettingCenterAtom, setSettingCenterAtom } from '@/js/editor/state/editorUiAtoms.ts'

type CenterProps = {
    onChange: (center: [number, number]) => void
    mapType: 'image' | 'geographic' | null
}

export const Center: FC<CenterProps> = ({ onChange, mapType }) => {
    const [color, setColor] = useState('var(--color-mm-gray)')
    const map = useMap()
    const isSettingCenter = useAtomValue(isSettingCenterAtom)
    const setSettingCenter = useSetAtom(setSettingCenterAtom)

    const toggleSetCenterState = useCallback(() => {
        const mapContainer = document.querySelector('.leaflet-container')
        const menu = document.getElementById('mm-menu')

        mapContainer?.classList.toggle('!cursor-crosshair')
        menu?.classList.toggle('center-selection-active')

        setSettingCenter(!isSettingCenter)
    }, [isSettingCenter, setSettingCenter])

    useEffect(() => {
        if (isSettingCenter) {
            const handleMapClick = (event: LeafletMouseEvent) => {
                onChange([event.latlng.lat, event.latlng.lng])
                toggleSetCenterState()
            }

            map.addEventListener('click', handleMapClick)

            return () => {
                map.removeEventListener('click', handleMapClick)
            }
        }
    }, [isSettingCenter, map, onChange, toggleSetCenterState])

    return (
        <div className={`flex flex-col justify-between gap-1`}>
            <Label>{mapType === 'image' ? 'Mittelpunkt' : 'Koordinaten'} </Label>

            <div className={'grid grid-cols-2 text-sm'}>
                <div className={'grid grid-cols2'}>
                    <div className={'flex gap-3'}>
                        <span className={'text-slate-400'}>X</span>
                        <span>
                            {mapType === 'image' ? map.getCenter().lat.toFixed(4) : map.getCenter().lat.toFixed(8)}
                        </span>
                    </div>
                    <div className={'flex gap-3'}>
                        <span className={'text-slate-400'}>Y</span>
                        <span>
                            {mapType === 'image' ? map.getCenter().lng.toFixed(4) : map.getCenter().lng.toFixed(8)}
                        </span>
                    </div>
                </div>

                <div className={'flex justify-end'}>
                    <Button className={'w-fit h-fit p-2'} variant={'outline'} onClick={toggleSetCenterState}>
                        <Crosshair
                            color={color}
                            className={'cursor-pointer transition-all'}
                            onMouseEnter={() => setColor('var(--color-mm-gray2)')}
                            onMouseLeave={() => setColor('var(--color-mm-gray)')}
                        />
                    </Button>
                </div>
            </div>
        </div>
    )
}
